/* eslint-disable jsx-a11y/control-has-associated-label */
import {useMemo, useState} from 'react';

import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import FontPicker from 'features/postGenerator/FontPicker/FontPicker';
import FontSizePicker from 'features/postGenerator/FontSizePicker/FontSizePicker';
import {setFieldsAC, setFontAC, setSingleFieldAC} from 'features/postGenerator/slice';
import {IField} from 'features/postGenerator/typescript';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import cloneDeep from 'lodash.clonedeep';
import {setModalAC} from 'store/modal/modalSlice';

import './TemplateStyle.scss';

const colors = [
  '#000000',
  '#252525',
  '#585858',
  '#7E7E7E',
  '#BCBCBC ',
  '#FFFFFF ',
  '#FD0000 ',
  '#FFBF17 ',
  '#FEFF01 ',
  '#02B24D ',
  '#0073BE',
];

export default function TemplateStyle() {
  const {singleField, fields, font} = useGlobalState((state) => state.postGenerator);
  const [draftField, setDraftField] = useState<IField>(singleField || {id: '', style: {fontSize: '14'}});
  const maxChars = 40;

  const charsLeft = useMemo(() => {
    if (!draftField.value) return 40;

    return maxChars - draftField.value.length;
  }, [draftField.value]);

  const dispatch = useSetGlobalState();

  const onSave = () => {
    if (singleField) {
      const newFields = fields.map((field) => {
        if (field.id === draftField.id) {
          field = draftField;
        }

        return field;
      });

      dispatch(setFieldsAC(newFields));
    }

    dispatch(setSingleFieldAC({id: '', style: {fontSize: '1'}}));
    dispatch(setModalAC(null));
  };

  const onCancel = () => {
    dispatch(setModalAC(null));
  };

  const handleValueSizeChange = (value: number) => {
    setDraftField((prev) => {
      const newState = cloneDeep(prev);
      newState.style.fontSize = value;

      return newState;
    });
  };

  return (
    <div className="template-style">
      <div className="template-style__container">
        <h1 className="template-style__title">Add text</h1>
        <div className="template-style__group template-style__group--text">
          <textarea
            style={{...draftField.style, fontSize: `${draftField.style.fontSize}px`}}
            value={draftField?.value}
            placeholder="Type here..."
            onChange={(e) => {
              setDraftField((prev) => {
                const newState = cloneDeep(prev);
                newState.value = e.target.value;

                return newState;
              });
            }}
            maxLength={maxChars}
          />
          <p>{charsLeft} Characters left</p>
        </div>
        <div className="template-style__group template-style__group--font">
          <div>
            <h3>Font</h3>
            <FontPicker
              className={`template-style__font-picker ${font ? 'template-style__font-picker--selected' : ''}`}
              onChange={(nextFont) => {
                dispatch(setFontAC(nextFont));
                setDraftField((prev) => {
                  const newState = cloneDeep(prev);
                  newState.style.fontFamily = nextFont.family;

                  return newState;
                });
              }}
            />
          </div>
          <div>
            <h3>Size</h3>
            <FontSizePicker value={draftField.style.fontSize} handleChange={handleValueSizeChange} />
          </div>
          <div className="template-style__align-wrapper">
            <Button
              variant="default"
              className={`template-style__align-text ${
                draftField.style.textAlign === 'left' || !draftField.style.textAlign
                  ? 'template-style__align-text--active'
                  : ''
              }`}
              onClick={() => {
                setDraftField((prev) => {
                  const newState = cloneDeep(prev);
                  newState.style.textAlign = 'left';

                  return newState;
                });
              }}
            >
              <Icon icon="left-align" />
            </Button>
            <Button
              variant="default"
              className={`template-style__align-text ${
                draftField.style.textAlign === 'center' ? 'template-style__align-text--active' : ''
              }`}
              onClick={() => {
                setDraftField((prev) => {
                  const newState = cloneDeep(prev);
                  newState.style.textAlign = 'center';

                  return newState;
                });
              }}
            >
              <Icon icon="center-align" />
            </Button>
            <Button
              variant="default"
              className={`template-style__align-text ${
                draftField.style.textAlign === 'right' ? 'template-style__align-text--active' : ''
              }`}
              onClick={() => {
                setDraftField((prev) => {
                  const newState = cloneDeep(prev);
                  newState.style.textAlign = 'right';

                  return newState;
                });
              }}
            >
              <Icon icon="right-align" />
            </Button>
          </div>
        </div>
        <div className="template-style__group template-style__group--color">
          <h3>Color</h3>
          <div className="template-style__group-inline">
            {colors.map((color) => {
              return (
                <Button
                  key={color}
                  variant="default"
                  style={{backgroundColor: color}}
                  onClick={() => {
                    setDraftField((prev) => {
                      const newState = cloneDeep(prev);
                      newState.style.color = color;

                      return newState;
                    });
                  }}
                />
              );
            })}
          </div>
        </div>
        <div className="template-style__group template-style__group--controls">
          <Button variant="empty" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} disabled={draftField.value === ''}>
            Add Text
          </Button>
        </div>
      </div>
    </div>
  );
}
